import React, {useCallback, useEffect, useState} from "react";
import {Link as RouterLink, useNavigate, useParams} from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from "@mui/material";
import {useUserAuth} from "../../context/UserAuthContext";
import {
    acceptApplication,
    deleteProject,
    finishProject,
    getApprovedApplications,
    getApplications,
    getProject,
    rejectApplication,
    removeApprovedApplication,
} from "../../services/associationService";
import PortalTopbar from "./PortalTopbar";
import {ProjectAvatar} from "../common/ProjectImage";

const PRIMARY = "#3E4F88";

/**
 * Detalhe de projeto da associação — espelha o ProjectDetailsPage +
 * ApplicantDetailsPage da app:
 *   - candidaturas pendentes (Aceitar / Rejeitar, com confirmação)
 *   - engenheiros aprovados (Remover, com confirmação)
 *   - Concluir projeto (+1 ponto aos aprovados, emails, apaga o projeto)
 *   - Apagar projeto (apaga projeto + candidaturas + imagem)
 * As ações só são permitidas à associação dona do projeto.
 */
const ProjectDetailsPage = () => {

    const {id} = useParams();
    const {profile} = useUserAuth();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [applications, setApplications] = useState([]);
    const [approved, setApproved] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [actionError, setActionError] = useState("");
    const [success, setSuccess] = useState("");
    const [confirm, setConfirm] = useState(null); // {title, text, confirmLabel, danger, onConfirm}

    const load = useCallback(async () => {
        try {
            const p = await getProject(id);
            setProject(p);
            if (p && p.Id) {
                const [apps, ap] = await Promise.all([
                    getApplications(p.Id),
                    getApprovedApplications(p.Id),
                ]);
                setApplications(apps);
                setApproved(ap);
            }
            setError("");
        } catch (err) {
            console.error("Erro ao carregar projeto:", err);
            setError("Não foi possível carregar o projeto.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const isOwner = project && profile && project.Association === profile.Name;

    const withProjectTitle = (a) => ({...a, _projectTitle: project.Title});

    const runConfirm = (title, text, confirmLabel, danger, onConfirm) => {
        setActionError("");
        setSuccess("");
        setConfirm({title, text, confirmLabel, danger, onConfirm});
    };

    const handleAccept = (applicant) => {
        runConfirm(
            "Aceitar Candidatura",
            "Deseja aceitar a candidatura para este projeto?",
            "Aceitar",
            false,
            async () => {
                try {
                    await acceptApplication(withProjectTitle(applicant), project.Id);
                    setApplications((list) => list.filter((a) => a.Id !== applicant.Id));
                    setApproved((list) => [...list, applicant]);
                    setSuccess("Candidatura aceite!");
                } catch (err) {
                    console.error(err);
                    setActionError("Algo aconteceu. Tenta novamente.");
                }
            }
        );
    };

    const handleReject = (applicant) => {
        runConfirm(
            "Remover Candidatura",
            "Deseja remover a candidatura a este projeto?",
            "Remover",
            true,
            async () => {
                try {
                    await rejectApplication(withProjectTitle(applicant), project.Id);
                    setApplications((list) => list.filter((a) => a.Id !== applicant.Id));
                    setSuccess("Candidatura removida!");
                } catch (err) {
                    console.error(err);
                    setActionError("Algo aconteceu. Tenta novamente.");
                }
            }
        );
    };

    const handleRemoveApproved = (applicant) => {
        runConfirm(
            "Remover engenheiro",
            "Deseja remover a candidatura a este projeto?",
            "Remover",
            true,
            async () => {
                try {
                    await removeApprovedApplication(withProjectTitle(applicant), project.Id);
                    setApproved((list) => list.filter((a) => a.Id !== applicant.Id));
                    setSuccess("Engenheiro removido!");
                } catch (err) {
                    console.error(err);
                    setActionError("Algo aconteceu. Tenta novamente.");
                }
            }
        );
    };

    const handleFinish = () => {
        runConfirm(
            "Concluir projeto",
            "Deseja dar este projeto como concluído? Os engenheiros aprovados recebem 1 ponto e o projeto é removido.",
            "Concluir",
            false,
            async () => {
                try {
                    await finishProject(project);
                    setSuccess("Projeto concluído!");
                    setTimeout(() => navigate("/app/associacao"), 800);
                } catch (err) {
                    console.error(err);
                    setActionError("Não foi possível concluir o projeto na base de dados.");
                }
            }
        );
    };

    const handleDelete = () => {
        runConfirm(
            "Apagar projeto",
            "Deseja apagar este projeto? As candidaturas e a imagem também serão apagadas.",
            "Apagar",
            true,
            async () => {
                try {
                    await deleteProject(project);
                    setSuccess("Projeto apagado com sucesso!");
                    setTimeout(() => navigate("/app/associacao"), 800);
                } catch (err) {
                    console.error(err);
                    setActionError("Não foi possível apagar o projeto na base de dados.");
                }
            }
        );
    };

    // ---------- estados de carregamento / erro ----------
    if (loading) {
        return (
            <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
                <PortalTopbar/>
                <Box sx={{display: "flex", justifyContent: "center", py: 10}}>
                    <CircularProgress/>
                </Box>
            </Box>
        );
    }

    if (!project) {
        return (
            <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
                <PortalTopbar/>
                <Container maxWidth="sm" sx={{py: 8}}>
                    <Stack spacing={3} alignItems="center">
                        <Alert severity={error ? "error" : "warning"}>
                            {error || "Projeto não encontrado. Talvez já tenha sido apagado."}
                        </Alert>
                        <Button component={RouterLink} to="/app/associacao" variant="contained" sx={{bgcolor: PRIMARY, fontWeight: 600}}>
                            Voltar à lista
                        </Button>
                    </Stack>
                </Container>
            </Box>
        );
    }

    if (!isOwner) {
        return (
            <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
                <PortalTopbar/>
                <Container maxWidth="sm" sx={{py: 8}}>
                    <Stack spacing={3} alignItems="center">
                        <Alert severity="warning">
                            Este projeto pertence a outra associação e não podes gerir a partir da tua conta.
                        </Alert>
                        <Button component={RouterLink} to="/app/associacao" variant="contained" sx={{bgcolor: PRIMARY, fontWeight: 600}}>
                            Voltar à lista
                        </Button>
                    </Stack>
                </Container>
            </Box>
        );
    }

    // ---------- página ----------
    return (
        <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
            <PortalTopbar/>
            <Container maxWidth="lg" sx={{py: 5}}>
                <Stack spacing={3}>
                    <Button component={RouterLink} to="/app/associacao" variant="text" sx={{color: PRIMARY, fontWeight: 600, alignSelf: "flex-start"}}>
                        ← Voltar à lista de projetos
                    </Button>

                    {actionError && <Alert severity="error">{actionError}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    {/* Cartão do projeto */}
                    <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 3, display: "flex", gap: 3, flexDirection: {xs: "column", sm: "row"}, alignItems: {sm: "center"}}}>
                        <ProjectAvatar src={project.Logo} alt={project.Title} size={88}/>
                        <Box sx={{flex: 1}}>
                            <Typography variant="h4" sx={{fontWeight: 700, color: PRIMARY, mb: 1}}>
                                {project.Title}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mb: 1.5}}>
                                <Chip label={`🎓 ${project.College}`} size="small"/>
                                <Chip label={`📍 ${project.Location}`} size="small"/>
                                <Chip label={project.Time || "—"} size="small"/>
                                <Chip label={project.Type || "—"} size="small"/>
                                <Chip label={`👥 ${approved.length} aprovado(s)`} size="small" color="primary" variant="outlined"/>
                            </Stack>
                            {project.Disc ? (
                                <Typography variant="body2" color="text.secondary" sx={{whiteSpace: "pre-line"}}>
                                    {project.Disc}
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="text.disabled">Sem descrição.</Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Candidaturas pendentes */}
                    <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 3}}>
                        <Typography variant="h6" sx={{fontWeight: 700, color: PRIMARY, mb: 2}}>
                            Candidaturas pendentes ({applications.length})
                        </Typography>
                        {applications.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Ainda não há candidaturas para este projeto.
                            </Typography>
                        ) : (
                            <Stack spacing={2}>
                                {applications.map((a) => (
                                    <Box key={a.Id} sx={{display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", p: 2, border: 1, borderColor: "divider", borderRadius: 2}}>
                                        <Box sx={{flex: 1, minWidth: 220}}>
                                            <Typography variant="body1" sx={{fontWeight: 600}}>
                                                {a.FirstName} {a.LastName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {[a.Course, a.OENumber ? `OE ${a.OENumber}` : null, a.Company].filter(Boolean).join(" · ") || "—"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {[a.PhoneNumber, a.Email].filter(Boolean).join(" · ") || "—"}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={1}>
                                            <Button variant="contained" size="small" sx={{bgcolor: "#2e7d32"}} onClick={() => handleAccept(a)}>
                                                Aceitar
                                            </Button>
                                            <Button variant="outlined" size="small" color="error" onClick={() => handleReject(a)}>
                                                Rejeitar
                                            </Button>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    {/* Engenheiros aprovados */}
                    <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 3}}>
                        <Typography variant="h6" sx={{fontWeight: 700, color: PRIMARY, mb: 2}}>
                            Engenheiros aprovados ({approved.length})
                        </Typography>
                        {approved.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Ainda não há engenheiros aprovados.
                            </Typography>
                        ) : (
                            <Stack spacing={2}>
                                {approved.map((a) => (
                                    <Box key={a.Id} sx={{display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", p: 2, border: 1, borderColor: "divider", borderRadius: 2}}>
                                        <Box sx={{flex: 1, minWidth: 220}}>
                                            <Typography variant="body1" sx={{fontWeight: 600}}>
                                                {a.FirstName} {a.LastName}
                                                <Chip label={`${a.Points || 0} pt(s)`} size="small" sx={{ml: 1}}/>
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {[a.Course, a.OENumber ? `OE ${a.OENumber}` : null, a.Company].filter(Boolean).join(" · ") || "—"}
                                            </Typography>
                                        </Box>
                                        <Button variant="outlined" size="small" color="error" onClick={() => handleRemoveApproved(a)}>
                                            Remover
                                        </Button>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    <Divider/>

                    {/* Ações do projeto */}
                    <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                        <Button
                            variant="contained"
                            startIcon="✅"
                            sx={{bgcolor: PRIMARY, fontWeight: 600}}
                            onClick={handleFinish}
                        >
                            Concluir projeto
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon="🗑️"
                            onClick={handleDelete}
                        >
                            Apagar projeto
                        </Button>
                    </Stack>
                </Stack>
            </Container>

            {/* Diálogo de confirmação */}
            <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
                <DialogTitle>{confirm ? confirm.title : ""}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirm ? confirm.text : ""}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)} color="inherit">
                        Não
                    </Button>
                    <Button
                        onClick={async () => {
                            const action = confirm && confirm.onConfirm;
                            setConfirm(null);
                            if (action) await action();
                        }}
                        variant="contained"
                        color={confirm && confirm.danger ? "error" : "primary"}
                    >
                        {confirm ? confirm.confirmLabel : "Sim"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProjectDetailsPage;